import { css, cx } from '@linaria/core'
import { type FC, memo } from 'react'
import type { AppProductVariant } from '#app/types/AppTypes'
import { Icon } from '#components/Icon'
import InfoIcon from '#icons/info-circle-outline-24.svg'
import MinusIcon from '#icons/minus-24.svg'
import PlusIcon from '#icons/plus-24.svg'
import type { FamilyDataEntry } from '#pages/Configuration/Comparison/useLinearizeComparisonData'
import { style } from '#styles/index'
import { useCurrencyFormatter } from '#utils/formatCurrency'
import type { ChangeTableContext } from '../useChangesTable'

const rowClass = css`
  display: grid;
  grid-column: 1/13;
  grid-template-columns: subgrid;

  border-bottom: 1px solid ${style.themeColors.lineDefault};
  
  transition: ease 0.25s background-color;
  background: ${style.themeColors.backgroundDefault};
  --left-background: ${style.themeColors.backgroundBoxed};
  &:hover {
    background: ${style.themeColors.backgroundBoxed};
    --left-background: ${style.themeColors.backgroundBoxedHover};
  }

  &._selectable {
    cursor: pointer;
  }


  > * {
    padding: 8px 0;
    display: flex;
    align-content: center;
    align-items: center;
  }

  > ._id-left {
    padding-left: 25px;
    min-width: 7em;
    color: color-mix(in srgb, ${style.themeColors.textDefault} 70%, transparent);
  }

  > ._family-name-right {
    padding-left: 12px;
    width: 1fr;
  }

  > ._variant-name-left {
    padding-left: 12px;
    width: 2fr;
  }
  
  > ._info-icon-left {
    cursor: pointer;
    padding-left: 12px;
    width: auto;
    color: color-mix(in srgb, ${style.themeColors.textDefault} 70%, transparent);
    transition: ease 0.25s color;
    &:hover {
      color: ${style.themeColors.textDefault};
    }
  }

  
  > ._delta-price-left {
    padding-right: 9px;
    width: 100%;
    min-width: 6em;
    
    display: flex;
    justify-content: flex-end;
  }
  
  > ._expense-delta-left {
    padding: 0 0 0 5px;
    display: flex;
    justify-content: flex-end;
    width: 20px;

    > ._indicator {
      height: 100%;
      background-color: ${style.themeColors.textImportant};
      opacity: 0.5;
    }
    
  }
  
  // now we push the layout
  > ._expense-delta-right {
    display:flex;
    justify-content: flex-start;
    padding: 0 5px 0 0;
    width: 20px; 
    height: 100%;
    
    > ._indicator {
      height: 100%;
      background-color: ${style.themeColors.textDefault};
      opacity: 0.5;
    }
    
  }
  
  > ._id-right {
    padding-right: 0px;
    display: flex;
    gap: 4px;
    width: minmax(min-content, auto-fill);
    min-width: 7em;
    color: color-mix(in srgb, ${style.themeColors.textDefault} 70%, transparent);
  }
  
  > ._variant-name-right {
    padding-right: 8px;
    width: minmax(2fr, auto-fill);
  }
  
  > ._info-icon-right {
    cursor: pointer;
    padding-right: 8px;
    width: minmax(24px, auto-fill);
    color: color-mix(in srgb, ${style.themeColors.textDefault} 70%, transparent);
    transition: ease 0.25s color;

    &:hover {
      color: ${style.themeColors.textDefault};
    }
  }

  > ._price-right {
    padding-right: 12px;
    width: min-content;
    min-width: 4em;
  
    display: flex;
    justify-content: flex-end;
  }
  

  > [class$="right"] {

  }
  > [class$="left"] {
    background-color: var(--left-background);
    color: ${style.themeColors.textDimmed};
    transition: ease 0.25s background-color;
  }
`

const makeId = (variant: AppProductVariant | undefined) => (variant ? `${variant.parentFamily.id}${variant.id}` : undefined)
const makePrice = (price: number | undefined, delta: boolean, formatter: ReturnType<typeof useCurrencyFormatter>) =>
  price
    ? delta
      ? formatter.formatDeltaCurrency(price, { hideCurrencySymbol: true })
      : formatter.formatCurrency(price, { hideCurrencySymbol: true })
    : '–'

export const ChangeFamilyRow: FC<{ entry: FamilyDataEntry; context: ChangeTableContext }> = memo(function ComparisonFamilyRow({ entry, context }) {
  const baseChange = entry.base?.selectedVariant?.path ? context.diff?.byPath[entry.base?.selectedVariant?.path] : undefined
  const baseSelection = entry.base?.selectedVariant
  const comparisonSelection = entry.comparison?.selectedVariant

  const familyTitle = entry.base?.title ?? entry.comparison?.title

  const deltaPrice = context.priceDeltas.byPath[entry.id]
  const maxDelta = context.priceDeltas.maximumDelta
  // minimum 10% if there is a change, otherwise the line is a very thin hairline

  const indicatorWidth = Math.abs(deltaPrice) < 1 ? 0 : Math.max(10, (deltaPrice / maxDelta) ** 2 * 100)
  const currency = useCurrencyFormatter()

  const hasBaseCosima = entry.base?.selectedVariant?.cosima
  const hasComparisonCosima = entry.base?.variants.find((x) => x.id === entry.comparison?.selectedVariant?.id)?.cosima

  return (
    <div className={cx('_row', rowClass, baseChange?.change)}>
      <div className="_id-left">{makeId(comparisonSelection)}</div>
      <div className="_variant-name-left">{comparisonSelection?.title}</div>
      <div className="_info-icon-left">
        {hasComparisonCosima && (
          <Icon
            iconUrl={InfoIcon}
            size="m"
            onClick={(evt) => {
              evt.preventDefault()
              context.setHighlightEntry(entry, 'comparison')
            }}
          />
        )}
      </div>
      <div className="_delta-price-left">{baseSelection && makePrice(deltaPrice, true, currency)}</div>
      <div className="_expense-delta-left">{deltaPrice > 0 && <div className="_indicator" style={{ width: `${indicatorWidth}%` }} />}</div>
      <div className="_expense-delta-right">{deltaPrice < 0 && <div className="_indicator" style={{ width: `${indicatorWidth}%` }} />}</div>
      <div className="_id-right">
        <Icon iconUrl={baseChange?.change === 'added' ? PlusIcon : baseChange?.change === 'removed' ? MinusIcon : undefined} size="xs" />

        {makeId(baseSelection)}
      </div>
      <div className="_family-name-right">{familyTitle}</div>
      <div className="_variant-name-right">{baseSelection?.title}</div>
      <div className="_info-icon-right">
        {hasBaseCosima && (
          <Icon
            iconUrl={InfoIcon}
            size="m"
            onClick={(evt) => {
              evt.preventDefault()
              context.setHighlightEntry(entry, 'base')
            }}
          />
        )}
      </div>
      <div className="_price-right">{makePrice(baseSelection?.priceData?.price, false, currency)}</div>
    </div>
  )
})
